<template>
  <DModal :show-close="false" :isBgOverlay="true" :placeInTop="true" @close="$emit('close')">
    <div class="d-modal" :class="{'d-modal--with-sidebar': isShowQuotasSidebar && isQuotaVisible}" slot="insertComponent">
      <div class="d-modal__header">
        <h2>{{ i18n['absence_request'] }}</h2>
        <div class="d-modal__close icon__bg-circle" @click="$emit('close')">
          <span class="icon-cross2"></span>
        </div>
      </div>
      <div class="user-request__body">
        <div class="d-modal__content">
          <Tabs
            v-if="isShowTabs"
            class="marg-1-bottom"
            :selected="selectedTab"
            :tabs="[
              { id: 'request', title: i18n['tab_request'] },
              { id: 'list', title: i18n['tab_list'] }
            ]"
            @select="selectedTab = $event">
          </Tabs>

          <UserRequestList v-if="selectedTab === 'list'" :isUserView="true"></UserRequestList>

          <template v-if="selectedTab === 'request'">
            <template v-if="isMultipleSchoolsActive && !activeSchoolId">
              <div class="d-group">
                <label class="d-label">{{ i18n['select_school'] }}</label>
                <select v-model="activeSchoolId" @change="onChangeActiveSchoolId">
                  <option v-for="school in userSchoolsSorted" :value="school.id">
                    {{ school.name }}
                  </option>
                </select>
              </div>
            </template>

            <template v-else-if="activeSchoolId">
              <div class="do-spinner__container">
                <div class="do-spinner do-spinner--large"></div>
              </div>
            </template>

            <template v-else>
              <UserAbsence
                :resetCounter="resetAbsenceCounter"
                @setSubject="setSelectedSubject">
              </UserAbsence>

              <div class="d-group">
                <label class="d-label">{{ i18n['date'] }}</label>
                <DatepickerRange
                  :allowSameDate="true"
                  :startdate="date"
                  @startchanged="setStartDate"
                  @endchanged="setEndDate">
                </DatepickerRange>
              </div>

              <div class="time-changer__group">
                <label class="d-label">{{ i18n['time'] }}</label>
                <TimeChanger
                  :showIcon="true"
                  :start="startTime"
                  :end="endTime"
                  :isFiveMinutesInterval="false"
                  @updateStart="setStartTime"
                  @updateEnd="setEndTime">
                </TimeChanger>
              </div>

              <div class="d-group marg-1-top">
                <label class="d-label">{{ i18n['optional_note'] }}</label>
                <textarea class="do-input" v-model="note"></textarea>
              </div>
            </template>
          </template>
        </div>

        <div class="d-modal__buttons d-modal__buttons__padding"
             v-if="selectedTab === 'request'"
             style="padding: 1rem;">
          <button class="do-bttn" @click="$emit('close')">{{ $t('vue2.common.cancel') }}</button>

          <button class="do-bttn do-bttn--submit do-bttn--submit--disabled" v-if="!isValidated">
            {{ $t('vue2.common.create') }}
          </button>

          <button
            class="do-bttn do-bttn--submit"
            :class="{'do-bttn--submit--loading': isSubmitting}"
            v-else
            @click="submit">
            {{ $t('vue2.common.create') }}
          </button>
        </div>

        <aside v-if="isShowQuotasSidebar" v-show="isQuotaVisible" class="user-request__sidebar">
          <UserQuota
            :userId="user.id"
            :isSidebar="true"
            @visibility="isQuotaVisible = $event">
          </UserQuota>
        </aside>
      </div>
    </div>
  </DModal>
</template>

<script>
import docendo from '../../docendo'
import DModal from '../common/DModal'
import eventbus from '../../eventbus'
import { mapGetters } from 'vuex'
import moment from 'moment'
import DatepickerRange from '../datepicker/DatePickerRange.vue'
import TimeChanger from '../TimeChanger.vue'
import UserAbsence from './UserAbsence.vue'
import Tabs from '../common/Tabs'
import UserRequestList from './UserRequestList.vue'
import UserQuota from '../Salary/KMD/UserQuota.vue'

const I18N = {
  da: {
    absence_request: 'Fraværsanmodning',
    tab_request: 'Anmod om fravær',
    tab_list: 'Dine fraværsanmodninger',
    select_school: 'Vælg skole',
    date: 'Dato',
    time: 'Tid',
    optional_note: 'Evt. note',
    toast_created: 'Anmodning oprettet'
  },
  en: {
    absence_request: 'Absence request',
    tab_request: 'Request absence',
    tab_list: 'Your absence requests',
    select_school: 'Select school',
    date: 'Date',
    time: 'Time',
    optional_note: 'Optional note',
    toast_created: 'Request created'
  },
  nb: {
    absence_request: 'Fraværsforespørsel',
    tab_request: 'Send forespørsel',
    tab_list: 'Dine fraværsforespørsler',
    select_school: 'Velg skole',
    date: 'Dato',
    time: 'Tid',
    optional_note: 'Valgfri merknad',
    toast_created: 'Forespørsel opprettet'
  }
}

export default {
  components: { UserRequestList, UserAbsence, UserQuota, DatepickerRange, TimeChanger, DModal, Tabs },
  props: {
    isShowTabs: {
      type: Boolean,
      default: true
    },
    editRequest: {
      default: null
    }
  },
  data () {
    return {
      date: moment(),
      startDate: moment(),
      endDate: moment(),
      startTime: moment().set({ hour: 8, minutes: 0 }),
      endTime: moment().set({ hour: 16, minutes: 0 }),
      selectedSubject: null,
      note: '',
      isSubmitting: false,
      activeSchoolId: null,
      isMultipleSchoolsActive: false,
      selectedTab: 'request',
      resetAbsenceCounter: 0,
      isQuotaVisible: true
    }
  },
  computed: {
    ...mapGetters([
      'userCalendars',
      'schoolId',
      'schoolFeatures',
      'locale',
      'isKmdUserQuotaActive',
      'isPermissionsActivatedForSchool',
      'userPermissions'
    ]),
    i18n () {
      return I18N[this.locale] || I18N['da']
    },
    user () {
      return this.$store.state.user
    },
    userSchools () {
      return this.user && this.user.schools ? this.user.schools : []
    },
    userSchoolsSorted () {
      return this.userSchools.sort((a, b) => a.name.localeCompare(b.name))
    },
    isShowKmdQuotas () {
      return !!(this.user && this.user.id) &&
        this.isKmdUserQuotaActive &&
        this.userHasPermission('can_see_own_task_overview')
    },
    isShowQuotasSidebar () {
      return this.isShowKmdQuotas &&
        this.selectedTab === 'request' &&
        !this.activeSchoolId &&
        !this.isMultipleSchoolsActive
    },
    isValidated () {
      return this.selectedSubject
    },
    dbEventStart () {
      const date = moment(this.startDate).format('YYYY-MM-DD')
      const time = this.startTime.locale('LT')
      return moment(date).set({
        hour: time.get('hour'),
        minutes: time.get('minutes')
      })
    },
    dbEventEnd () {
      const date = moment(this.endDate).format('YYYY-MM-DD')
      const time = this.endTime.locale('LT')
      return moment(date).set({
        hour: time.get('hour'),
        minutes: time.get('minutes')
      })
    }
  },
  methods: {
    userHasPermission (permission) {
      if (!this.user) {
        return false
      }
      if (this.isPermissionsActivatedForSchool) {
        return this.userPermissions.indexOf(permission) > -1 || this.user.admin
      }
      return true
    },
    resetForm () {
      this.startDate = moment()
      this.endDate = moment()
      this.startTime = moment().set({ hour: 8, minutes: 0 })
      this.endTime = moment().set({ hour: 16, minutes: 0 })
      this.selectedSubject = null
      this.note = ''
      this.resetAbsenceCounter++
    },
    onChangeActiveSchoolId () {
      this.readSchoolYears()
      this.$nextTick(_ => {
        console.log(document.getElementById('app'))
      })
    },
    readSchoolYears () {
      this.isLoadingSchoolYears = true
      docendo.schoolYearsRead(this.activeSchoolId, {}, 'name', data => {
        this.schoolYears = data
        this.isLoadingSchoolYears = false
      })
    },
    setSelectedSubject (subject) {
      this.selectedSubject = subject
    },
    setStartDate (payload) {
      this.startDate = payload
    },
    setEndDate (payload) {
      this.endDate = payload
    },
    setStartTime (payload) {
      this.startTime = payload
    },
    setEndTime (payload) {
      this.endTime = payload
    },
    submit () {
      this.isSubmitting = true
      const schoolId = this.selectedSubject.school_id
      const startTime = this.dbEventStart
      const endTime = this.dbEventEnd
      const requestNote = this.note
      const subjectId = this.selectedSubject.id
      docendo.createRequest(schoolId, subjectId, startTime, endTime, requestNote, success => {
        this.showToast(this.i18n['toast_created'], true)
        this.isSubmitting = false
        this.selectedTab = 'list'
        this.resetForm()
        this.$store.dispatch('trackEvent', 'calendar_absence_request')
      }, failure => {
        this.showToast(failure, false)
        this.isSubmitting = false
      })
    },
    showToast (message, success) {
      eventbus.toast = { text: message, success }
    }
  }
}
</script>

<style scoped>
.d-modal {
  width: 850px;
  border-radius: 16px;
  position: relative;
}

.d-modal__header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0;
}

.d-modal__close {
  font-size: 16px !important;
}

.d-modal__buttons {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.d-modal__content {
  padding: 1rem;
  overflow-y: auto;
  max-height: calc(100vh - 2rem - 52px - 60px);
}

.d-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.do-spinner__container {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2rem;
}

textarea.do-input {
  min-height: 120px
}

.d-modal--with-sidebar {
  width: 1150px;
  max-width: calc(100vw - 2rem);
}

.d-modal--with-sidebar .user-request__body {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-areas: "content" "buttons" "sidebar";
  max-height: calc(100vh - 2rem - 52px);
  overflow-y: auto;
}

.d-modal--with-sidebar .d-modal__content {
  grid-area: content;
  max-height: none;
  overflow: visible;
}

.d-modal--with-sidebar .d-modal__buttons {
  grid-area: buttons;
}

.user-request__sidebar {
  grid-area: sidebar;
  padding: 1rem;
  border-top: 1px solid #e2e8f0;
  background: #fafbfc;
  border-radius: 0 0 16px 16px;
}

@media (min-width: 1000px) {
  .d-modal--with-sidebar .user-request__body {
    grid-template-columns: minmax(0, 1fr) 420px;
    grid-template-areas: "content sidebar" "buttons sidebar";
    grid-template-rows: minmax(0, 1fr) auto;
    overflow: hidden;
  }

  .d-modal--with-sidebar .d-modal__content {
    max-height: calc(100vh - 2rem - 52px - 60px);
    overflow-y: auto;
  }

  .user-request__sidebar {
    border-top: none;
    border-left: 1px solid #e2e8f0;
    border-radius: 0 0 16px 0;
    max-height: calc(100vh - 2rem - 52px);
    overflow-y: auto;
  }
}
</style>



// WEBPACK FOOTER //
// UserRequest.vue?3d430c00